import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { fmtDateTime } from "../lib/date";
import { Bell } from "@phosphor-icons/react";

const DOT = { critical: "bg-red-500", warning: "bg-amber-500", success: "bg-emerald-500", info: "bg-blue-500" };

// Bell with unread count + list. Polls every 60s and refreshes when the tab regains focus.
export function NotificationBell({ testid = "notification-bell", dark = true }) {
  const [count, setCount] = useState(0);
  const [urgent, setUrgent] = useState(0);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const nav = useNavigate();
  const refreshCount = useCallback(() => api.get("/notifications/unread-count").then((r) => { setCount(r.data.count); setUrgent(r.data.urgent || 0); }).catch(() => {}), []);
  const loadList = useCallback(() => api.get(`/notifications?unread_only=${unreadOnly}&limit=30`).then((r) => setRows(r.data)).catch(() => setRows([])), [unreadOnly]);
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60000);
    window.addEventListener("focus", refreshCount);
    return () => { clearInterval(t); window.removeEventListener("focus", refreshCount); };
  }, [refreshCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);
  const openItem = async (n) => {
    if (!n.is_read) { await api.post(`/notifications/${n.id}/read`).catch(() => {}); refreshCount(); }
    setOpen(false);
    if (n.link) nav(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all").catch(() => {}); setCount(0); setUrgent(0); loadList(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={testid} aria-label={`Notifications${count ? `, ${count} unread` : ""}${urgent ? `, ${urgent} urgent faults` : ""}`} className={`relative p-1.5 rounded-md transition-colors duration-150 ${urgent ? "ring-2 ring-red-500 animate-pulse" : ""} ${dark ? "text-slate-300 hover:text-white hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"}`}>
          <Bell size={22} weight={count ? "fill" : "bold"} className={urgent ? "text-red-400" : ""} />
          {urgent > 0 && <span data-testid={`${testid}-urgent`} title={`${urgent} urgent fault${urgent > 1 ? "s" : ""}`} className="absolute -bottom-1 -left-1 bg-red-600 text-white text-[10px] font-black rounded-full w-[16px] h-[16px] flex items-center justify-center border-2 border-slate-900">!</span>}
          {count > 0 && <span data-testid={`${testid}-count`} className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{count > 99 ? "99+" : count}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0 bg-white" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-display font-bold text-slate-900">Notifications</span>
          <div className="flex items-center gap-3 text-xs">
            <button data-testid="notif-toggle-unread" onClick={() => setUnreadOnly((v) => !v)} className="text-slate-500 hover:text-slate-900">{unreadOnly ? "Show all" : "Unread only"}</button>
            {count > 0 && <button data-testid="notif-read-all" onClick={readAll} className="font-semibold text-emerald-700">Mark all read</button>}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y">
          {rows === null ? <p className="p-4 text-sm text-slate-400">Loading…</p>
            : rows.length === 0 ? <p data-testid="notif-empty" className="p-6 text-center text-sm text-slate-500">{unreadOnly ? "You're all caught up." : "No notifications yet."}</p>
            : rows.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)} className={`w-full text-left px-3 py-2.5 flex gap-2.5 hover:bg-slate-50 ${n.is_read ? "opacity-60" : ""}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.urgent && !n.is_read ? "bg-red-600" : n.is_read ? "bg-slate-300" : DOT[n.severity] || DOT.info}`} />
                <span className="min-w-0">
                  {n.urgent && <span data-testid={`notif-urgent-${n.id}`} className="inline-block text-[10px] font-black uppercase bg-red-600 text-white rounded px-1.5 py-0.5 mb-0.5">Urgent · asset restricted</span>}
                  <span className="block text-sm font-semibold text-slate-800">{n.title}</span>
                  {n.body && <span className="block text-xs text-slate-600 line-clamp-2">{n.body}</span>}
                  <span className="block text-[11px] text-slate-400 mt-0.5">{fmtDateTime(n.created_at)}</span>
                </span>
              </button>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
